/**
 * 登录页面
 * 提供用户登录功能
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import { AUTH_ROUTES } from '../../../config/routes';
import backendStatusService from '../../../services/api/backendStatusService';
import LoginInfo from '../../../components/common/LoginInfo/LoginInfo';
import './LoginPage.css';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, isAuthenticated, loading } = useAuth();
  
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showLoginInfo, setShowLoginInfo] = useState(false);
  const [backendStatus, setBackendStatus] = useState<{
    isOnline: boolean;
    message: string;
    checking: boolean;
  }>({
    isOnline: false,
    message: '正在检查后台服务状态...',
    checking: true
  });

  // 异步检查后台服务状态 - 成功后停止，不重复调用
  useEffect(() => {
    let isMounted = true; // 防止组件卸载后的状态更新
    let hasChecked = false; // 防止重复检查

    const checkBackendAsync = async () => {
      // 如果已经检查过且成功，不再重复检查
      if (hasChecked) {
        return;
      }

      try {
        const status = await backendStatusService.checkBackendStatus();
        
        // 只有在组件仍然挂载时才更新状态
        if (isMounted) {
          setBackendStatus({
            isOnline: status.isOnline,
            message: status.message,
            checking: false
          });
          
          hasChecked = true; // 标记已检查
          
          if (status.isOnline) {
          } else {
          }
        }
      } catch (error) {
        console.error('❌ 后台服务状态检查异常:', error);
        
        // 只有在组件仍然挂载时才更新状态
        if (isMounted) {
          setBackendStatus({
            isOnline: false,
            message: '检查后台服务状态失败',
            checking: false
          });
          hasChecked = true; // 即使失败也标记为已检查，避免重复
        }
      }
    };

    // 立即执行异步检查
    checkBackendAsync();

    // 清理函数
    return () => {
      isMounted = false;
    };
  }, []); // 空依赖数组确保只在挂载时执行一次

  // 如果已经登录，跳转到用户有权限的第一个菜单
  useEffect(() => {
    if (isAuthenticated && !loading) {
      // 登录成功后，不跳转到redirect参数指定的路径
      // 而是跳转到用户有权限的第一个菜单
      // 使用 useMenuDataSimple 的缓存数据，避免重复调用接口
      const redirectToFirstMenu = async () => {
        try {
          // 导入菜单工具函数
          const { findFirstMenuPath } = await import('../../../utils/menuUtils');
          
          // 等待菜单加载完成（通过全局缓存）
          // 使用全局变量的方式，避免创建新的 hook 实例和重复调用接口
          let currentUserId: number | null = null;
          try {
            const userInfoStr = localStorage.getItem('user_info');
            if (userInfoStr) {
              const userInfo = JSON.parse(userInfoStr);
              currentUserId = userInfo?.id || null;
            }
          } catch (e) {
            // 忽略解析错误
          }
          
          const cacheKey: number | string | null = currentUserId;
          
          // 等待菜单数据加载完成（最多等待5秒，增加等待时间）
          let retries = 0;
          let menuData: any[] = [];
          const maxRetries = 50; // 增加到50次，每次100ms，总共5秒
          
          while (retries < maxRetries && menuData.length === 0) {
            // 检查全局缓存
            if (cacheKey) {
              const globalMenuDataMap = (window as any).__globalMenuDataMap__;
              const globalIsLoadedMap = (window as any).__globalIsLoadedMap__;
              const loadingPromise = (window as any).__loadingPromise__;
              
              if (globalMenuDataMap && globalIsLoadedMap) {
                const isLoaded = globalIsLoadedMap.get(cacheKey) || false;
                const cachedData = globalMenuDataMap.get(cacheKey);
                
                if (isLoaded && cachedData && cachedData.length > 0) {
                  menuData = cachedData;
                  break;
                }
              }
              
              // 如果正在加载，等待完成
              if (loadingPromise) {
                try {
                  await loadingPromise;
                  const cachedData = globalMenuDataMap?.get(cacheKey);
                  if (cachedData && cachedData.length > 0) {
                    menuData = cachedData;
                    break;
                  }
                } catch (e) {
                  // 忽略错误
                }
              }
            }
            
            // 等待一小段时间后重试
            await new Promise(resolve => setTimeout(resolve, 100));
            retries++;
          }
          
          // 如果从缓存获取到数据，使用缓存数据
          if (menuData.length > 0) {
            // 查找第一个有路径的菜单
            // 注意：menuData 可能是扁平数组，findFirstMenuPath 应该能处理
            // 如果后端返回的是树形结构，findFirstMenuPath 会递归查找
            const firstPath = findFirstMenuPath(menuData);
            if (firstPath) {
              // 跳转前重置提交状态（此时遮罩会消失，因为即将跳转）
              setIsSubmitting(false);
              navigate(firstPath, { replace: true });
              return;
            } else {
              // 如果有菜单数据但找不到有效路径，可能是菜单结构问题
              // 先跳转到首页，让用户选择菜单
              setIsSubmitting(false);
              navigate('/data/home', { replace: true });
              return;
            }
          }
          
          // 如果缓存中没有数据或没有找到菜单，可能是菜单加载慢或真的无权限
          // 先跳转到首页，如果用户真的无权限，首页会显示无权限信息
          setIsSubmitting(false);
          navigate('/data/home', { replace: true });
        } catch (error) {
          console.error('获取菜单失败:', error);
          // 如果获取菜单失败，跳转到默认页面
          setIsSubmitting(false);
          navigate(AUTH_ROUTES.DATA_MANAGEMENT, { replace: true });
        }
      };
      
      // 延迟一点确保token已设置
      setTimeout(() => {
        redirectToFirstMenu();
      }, 300);
    }
  }, [isAuthenticated, loading, navigate]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // 清除对应字段的错误
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};
    
    if (!formData.username.trim()) {
      newErrors.username = '请输入用户名';
    }
    
    if (!formData.password.trim()) {
      newErrors.password = '请输入密码';
    } else if (formData.password.length < 6) {
      newErrors.password = '密码至少6位';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // 如果正在提交，直接返回，防止重复提交
    if (isSubmitting || loading) {
      return;
    }
    
    if (!validateForm()) {
      return;
    }

    // 如果后台服务未启动，阻止登录
    if (!backendStatus.isOnline && !backendStatus.checking) {
      setErrors({
        submit: '未检测到后台服务，请确保后台服务已启动'
      });
      return;
    }
    
    // 清除之前的错误信息
    setErrors({});
    setIsSubmitting(true);
    
    try {
      await login(formData.username, formData.password);
      // 登录成功后，保持遮罩显示，直到跳转完成
      // useEffect 会在 isAuthenticated 变为 true 时处理跳转
      // 遮罩会一直保持，直到组件卸载或跳转完成
    } catch (error: any) {
      console.error('登录错误:', error);
      let errorMessage = error instanceof Error ? error.message : String(error);
      if (!errorMessage || errorMessage === 'undefined') {
        errorMessage = '登录失败，请检查用户名和密码';
      }
      
      // 如果是网络错误，提供更友好的提示
      if (errorMessage.includes('Failed to fetch') || errorMessage.includes('NetworkError') || errorMessage.includes('连接')) {
        errorMessage = '无法连接到后台服务，请确保后台服务已启动并运行在';
      } else if (errorMessage.includes('405')) {
        errorMessage = '登录接口配置错误，请联系管理员';
      } else if (errorMessage.includes('401')) {
        errorMessage = '用户名或密码错误';
      } else if (errorMessage.includes('403')) {
        errorMessage = '账户已被禁用，请联系管理员';
      }
      
      // 登录失败，显示错误消息，允许重试
      setErrors({
        submit: errorMessage
      });
      setIsSubmitting(false); // 登录失败后，允许重试
    }
    // 注意：登录成功时不重置 isSubmitting，让遮罩保持显示直到跳转完成
  };

  if (loading) {
    return (
      <div className="login-page">
        <div className="login-container">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>正在验证身份...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-container">
        {/* 登录等待遮罩 */}
        {isSubmitting && (
          <div 
            className="login-overlay"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onMouseUp={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onTouchStart={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onTouchEnd={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <div className="login-spinner">
              <div className="spinner"></div>
              <p>正在登录...</p>
            </div>
          </div>
        )}
        
        <div className="login-header">
          <h1>数据管理系统</h1>
          
          {/* 后台服务状态显示 - 只在服务异常时显示 */}
          {!backendStatus.isOnline && !backendStatus.checking && (
            <div className="backend-status offline">
              <div className="status-indicator">
                <div className="status-dot offline"></div>
                <span className="status-text">未检测到后台服务</span>
              </div>
              <div className="status-actions">
                <button 
                  className="retry-btn" 
                  onClick={() => window.location.reload()}
                  disabled={isSubmitting}
                >
                  重新检查
                </button>
                <p className="status-help">
                  请确保后台服务已启动并运行在 {backendStatusService.getBackendUrl()}
                </p>
              </div>
            </div>
          )}
        </div>
        
        <form 
          className="login-form" 
          onSubmit={handleSubmit}
          style={{ pointerEvents: isSubmitting ? 'none' : 'auto' }}
        >
          <div className="form-group">
            <div className="input-wrapper">
              <div className="input-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z" fill="#999"/>
                  <path d="M12 14C7.58172 14 4 17.5817 4 22H20C20 17.5817 16.4183 14 12 14Z" fill="#999"/>
                </svg>
              </div>
              <div className="input-separator"></div>
              <input
                type="text"
                id="username"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                className={`form-input ${errors.username ? 'error' : ''}`}
                placeholder="请输入用户名"
                disabled={isSubmitting}
                autoComplete="username"
              />
            </div>
            {errors.username && <span className="error-message">{errors.username}</span>}
          </div>
          
          <div className="form-group">
            <div className="input-wrapper">
              <div className="input-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 8H20C20.5523 8 21 8.44772 21 9V21C21 21.5523 20.5523 22 20 22H4C3.44772 22 3 21.5523 3 21V9C3 8.44772 3.44772 8 4 8H6V7C6 3.68629 8.68629 1 12 1C15.3137 1 18 3.68629 18 7V8ZM8 8H16V7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7V8ZM5 10V20H19V10H5Z" fill="#999"/>
                </svg>
              </div>
              <div className="input-separator"></div>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                className={`form-input ${errors.password ? 'error' : ''}`}
                placeholder="请输入密码"
                disabled={isSubmitting}
                autoComplete="current-password"
              />
            </div>
            {errors.password && <span className="error-message">{errors.password}</span>}
          </div>
          
          {errors.submit && (
            <div className="submit-error">
              {errors.submit}
            </div>
          )}
          
          <button
            type="submit"
            className="login-button"
            disabled={isSubmitting || loading}
          >
            {isSubmitting ? '登录中...' : '登录'}
          </button>
        </form>
      </div>
      
      {/* 登录信息提示 */}
      {showLoginInfo && (
        <LoginInfo onClose={() => setShowLoginInfo(false)} />
      )}
    </div>
  );
};

export default LoginPage;